import React from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getAllSensors } from "@modules/Sensors/services/sensorCrud";
import { getCustomerBasicInfo } from "@modules/Customer/services/customerCrud";
import { createScheduledQueryFn, REQUEST_PRIORITIES } from "@helper/requestPriority";

/**
 * Component that initializes background data queries
 * This runs within the QueryClientProvider context to pre-load data
 * Now with additional authentication and route protection
 */
const BackgroundDataLoader = () => {
  const location = useLocation();
  const isOnLoginPage = location.pathname === "/login";
  const authState = useSelector((state) => state.auth);
  const isAuthenticated = !!(authState?.authToken && authState?.user);

  // Chrome-specific: Check for rehydration stability
  const isChrome = typeof window !== "undefined" && !!window.chrome;
  const [isRehydrated, setIsRehydrated] = React.useState(!isChrome); // Non-Chrome browsers don't need to wait

  React.useEffect(() => {
    if (isChrome && authState) {
      // Small delay for Chrome to ensure Redux persist has fully rehydrated
      const timer = setTimeout(() => {
        setIsRehydrated(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isChrome, authState]);

  // Determine if we should enable background loading
  const shouldLoadData = isAuthenticated && !isOnLoginPage && authState && isRehydrated;

  console.debug("[BackgroundDataLoader]", {
    shouldLoadData,
    isAuthenticated,
    isOnLoginPage,
    hasAuthState: !!authState,
    isRehydrated,
    browser: isChrome ? "Chrome" : "Other",
  });

  // Initialize first page of sensors list with low priority (background loading)
  // Changed from loading all sensors to just the first page for faster initial load
  const scheduledGetAllSensors = createScheduledQueryFn(
    () => getAllSensors({ page: 1, pageSize: 20, search: "" }),
    REQUEST_PRIORITIES.LOW,
    "getAllSensors"
  );

  useQuery(["sensors", { page: 1, pageSize: 20, search: "" }], scheduledGetAllSensors, {
    enabled: shouldLoadData,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes (shorter than before since we're only loading one page)
    cacheTime: 1000 * 60 * 30, // 30 minutes
    retry: 1,
  });

  // Initialize customer basic info query with low priority (background loading)
  const scheduledGetCustomerBasicInfo = createScheduledQueryFn(
    getCustomerBasicInfo,
    REQUEST_PRIORITIES.LOW,
    "getCustomerBasicInfo"
  );

  useQuery("customerBasicInfo", scheduledGetCustomerBasicInfo, {
    enabled: shouldLoadData,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 30, // 30 minutes
    cacheTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
  });

  // Return null since this is just for data loading
  return null;
};

export default BackgroundDataLoader;
